import { TokenInfo } from '@labb/constellation-core-types';

import { API_ERROR_CODES } from '../../onboarding-api';
import { buildQueryParams, PegaStartingFields } from './buildQueryParams';

export enum PegaApplicationStatus {
  Empty = 'Empty',
  'Open-InProgress' = 'Open-InProgress',
  'Resolved-Completed' = 'Resolved-Completed',
}

export type PegaState = {
  applicationAssignmentKey: string;
  applicationStatus: PegaApplicationStatus;
};

export type PegaApplication = {
  applicationStatus: PegaApplicationStatus;
  caseID: string;
  applicationAssignmentKey: string;
  productName: string;
  productCode: string;
  productDescription: string;
  updateDateTime: string;
  tenantTemplateCode: string;
  onboardingRegion: string;
  country: string;
  customerID: string;
  customerType: string;
  sourcePlatform: string;
};

type PegaApplicationsListResponse = {
  applications: PegaApplication[];
};

export type PegaStateResponse = PegaState | PegaApplicationsListResponse;

function isApplicationsListResponse(data: PegaStateResponse): data is PegaApplicationsListResponse {
  return Array.isArray((data as PegaApplicationsListResponse).applications);
}

export function resolvePegaStateResponse(
  data: PegaStateResponse
): { kind: 'single'; state: PegaState } | { kind: 'list'; applications: PegaApplication[] } {
  if (isApplicationsListResponse(data)) {
    return { kind: 'list', applications: data.applications };
  }
  return { kind: 'single', state: data };
}

/** True when get-state has no launchable case (empty list or blank/Empty single). */
export function isEmptyPegaStateResponse(data: PegaStateResponse): boolean {
  const resolved = resolvePegaStateResponse(data);
  if (resolved.kind === 'list') {
    return resolved.applications.length === 0;
  }

  const status = resolved.state.applicationStatus?.trim?.() ?? resolved.state.applicationStatus;
  const assignmentKey = resolved.state.applicationAssignmentKey?.trim?.() ?? '';

  // Blank single payloads (e.g. `{}` or empty strings) are treated as Empty.
  const isEmptyStatus = !status || status === PegaApplicationStatus.Empty;

  return isEmptyStatus && !assignmentKey;
}

type ApiGetPegaStateResult = {
  data?: PegaStateResponse;
  errorCode?: API_ERROR_CODES;
};

export async function apiGetPegaState(
  baseUrl: string,
  pegaToken: TokenInfo,
  startingFields?: PegaStartingFields
): Promise<ApiGetPegaStateResult> {
  const queryParams = buildQueryParams(startingFields);
  const url = `${baseUrl}/api/nexus/v1/onboarding/application?${queryParams}`;

  return fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${pegaToken.access_token}`,
    },
  })
    .then(async (response) => {
      if (!response.ok) {
        if (response.status === 401) {
          return {
            errorCode: API_ERROR_CODES.PEGA_TOKEN_EXPIRED,
          };
        }

        return {
          errorCode: API_ERROR_CODES.GENERIC,
        };
      }

      const responseJson = await response.json();

      return { data: responseJson };
    })
    .catch(() => {
      return {
        errorCode: API_ERROR_CODES.GENERIC,
      };
    });
}
